/**
 * Validates if a string is a valid domain format
 * @param domain - The domain string to validate
 * @returns true if valid, false otherwise
 */
export const isValidDomain = (input: string): boolean => {
	if (!input) return false;

	// Trim and allow a single trailing dot (FQDN)
	let raw = input.trim();
	const hadTrailingDot = raw.endsWith(".");
	if (hadTrailingDot) raw = raw.slice(0, -1);

	// Allow an accidental scheme and/or www. prefix
	raw = raw.replace(/^(https?:\/\/)?(www\.)?/i, "");

	// Immediately reject URL-ish stuff: userinfo, ports, paths, queries, fragments, quotes, spaces
	if (/[/:@?#'"()[\]\s]/.test(raw)) return false;

	// IDN support: convert Unicode host to punycode using URL()
	// (safe now because we pre-rejected '@', '/', ':', etc.)
	// eslint-disable-next-line no-control-regex
	if (/[^\x00-\x7F]/.test(raw)) {
		try {
			raw = new URL(`http://${raw}`).hostname; // yields punycode (xn--...)
		} catch {
			return false;
		}
	}

	// Length checks
	if (raw.length === 0 || raw.length > 253) return false;

	const labels = raw.split(".");
	if (labels.length < 2) return false;

	// All labels (including subdomains): 1–63 chars, alnum + hyphen, no leading/trailing hyphen
	const labelRe = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)$/;

	// TLD: letters and numbers (≥2), or punycode A-label
	const tld = labels[labels.length - 1];
	if (!tld) return false; // Add null check for tld

	const tldRe = /^(?:[A-Za-z0-9]{2,}|xn--[A-Za-z0-9-]{3,})$/;

	// Validate TLD first
	if (!tldRe.test(tld)) return false;

	// Validate all other labels (subdomains and root domain)
	if (!labels.slice(0, -1).every((label) => labelRe.test(label))) return false;

	return true;
};

/**
 * Cleans a domain string by removing protocol prefixes and www
 * @param domain - The domain string to clean
 * @returns Cleaned domain string
 */
export const cleanDomain = (domain: string): string => {
	return domain.replace(/^(https?:\/\/)?(www\.)?/, "");
};

/**
 * Validates and cleans a domain string
 * @param domain - The domain string to validate and clean
 * @returns Object with validation result and cleaned value
 */
export const validateAndCleanDomain = (domain: string) => {
	const cleaned = cleanDomain(domain);
	const isValid = isValidDomain(cleaned);

	return {
		isValid,
		cleaned,
		error: isValid
			? null
			: "Please enter a valid domain (e.g., example.com or subdomain.example.com)",
	};
};
